import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut, ChevronDown, MessageCircle, Search } from 'lucide-react';
import LogoImage from '../3.PNG';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';

// Firebase configuration using environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

const Header = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const mobileMenuRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up authentication state observer
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      
      // Set up messages listener when user changes
      if (currentUser) {
        setupMessagesListener(currentUser.uid);
      } else {
        setUnreadMessagesCount(0);
      }
    });

    // Clean up the observer when component unmounts
    return () => unsubscribe();
  }, []);

  const setupMessagesListener = (userId) => {
    console.log("Setting up messages listener for user:", userId);
    
    // Listen to user's chats collection for unread messages
    const userChatsRef = collection(db, 'users', userId, 'chats');
    
    // Set up real-time listener for user's chats
    const unsubscribe = onSnapshot(userChatsRef, (snapshot) => {
      let totalUnread = 0;
      
      snapshot.forEach((doc) => {
        const chatData = doc.data();
        if (chatData.unread === true) {
          totalUnread += 1;
        }
      });
      
      console.log("Total unread conversations:", totalUnread);
      setUnreadMessagesCount(totalUnread);
    }, (error) => {
      console.error("Error listening to user chats:", error);
      setUnreadMessagesCount(0);
    });
  
    return unsubscribe;
  };
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setMobileMenuOpen(false);
      setProfileDropdownOpen(false);
      navigate('/signup');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
    setMobileMenuOpen(false);
  };

  const handleMobileNavClick = (tab) => {
    setActiveTab(tab);
    setMobileMenuOpen(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        mobileMenuRef.current && 
        !mobileMenuRef.current.contains(event.target) && 
        !event.target.closest('button[aria-label="Toggle mobile menu"]')
      ) {
        setMobileMenuOpen(false);
      }
      
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setProfileDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-stone-100 backdrop-blur-sm shadow-sm border-b border-stone-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          
          {/* Logo Section */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-3 group"
              onClick={() => setActiveTab('home')}
            >
              <div className="relative">
                <img 
                  src={LogoImage} 
                  alt="Vine Matched Logo" 
                  className="w-8 h-8 lg:w-10 lg:h-10 transition-transform duration-200 group-hover:scale-105" 
                />
              </div>
              <div className="flex flex-col">
                <span 
                  className="text-lg lg:text-xl font-light tracking-wide"
                  style={{ 
                    fontFamily: "'Cormorant Garamond', serif",
                    background: 'linear-gradient(135deg, #8b7355, #d4b5a0)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  VineMatched
                </span>
                <span className="text-xs text-stone-500 font-light -mt-1 hidden sm:block tracking-wider uppercase">
                  Emotional Support
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {[
              { to: '/', label: 'Home', tab: 'home' },
             /* { to: '/howitworks', label: 'How It Works', tab: 'how' },
              { to: '/pricing', label: 'Pricing', tab: 'pricing' } */
            ].map((item) => (
              <Link
                key={item.tab}
                to={item.to}
                onClick={() => setActiveTab(item.tab)}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-stone-50 tracking-wide ${
                  activeTab === item.tab 
                    ? 'text-stone-700 bg-stone-100' 
                    : 'text-stone-600 hover:text-stone-800'
                }`}
              >
                {item.label}
                {activeTab === item.tab && (
                  <div 
                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ backgroundColor: '#8b7355' }}
                  ></div>
                )}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            
            {/* Messages and Search Users Buttons (only show when user is logged in) */}
            {user && (
              <div className="hidden lg:flex items-center space-x-2">
                {/* Search Users Button 
                <Link
                  to="/users"
                  onClick={() => setActiveTab('users')}
                  className={`relative px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-stone-50 tracking-wide flex items-center space-x-2 ${
                    activeTab === 'users' 
                      ? 'text-stone-700 bg-stone-100' 
                      : 'text-stone-600 hover:text-stone-800'
                  }`}
                  title="Search Users"
                >
                  <Search size={18} />
                  <span>Search</span>
                </Link> */}

                {/* Messages Button */}
                <Link
                  to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                  onClick={() => setActiveTab('chat')}
                  className={`relative px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-stone-50 tracking-wide flex items-center space-x-2 ${
                    activeTab === 'chat' 
                      ? 'text-stone-700 bg-stone-100' 
                      : 'text-stone-600 hover:text-stone-800'
                  }`}
                  title="Messages"
                >
                  <div className="relative">
                    <MessageCircle size={18} />
{/* In both desktop and mobile message links, update the badge */}
{unreadMessagesCount > 0 && (
  <div 
    className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full flex items-center justify-center text-xs font-bold text-white"
    style={{ backgroundColor: '#dc2626' }}
  >
    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
  </div>
)}
                  </div>
                  <span>Messages</span>
                </Link>
              </div>
            )}

            {/* Desktop User Section */}
            {user ? (
              <div className="hidden lg:flex items-center space-x-3">
                {/* Profile Dropdown */}
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={toggleProfileDropdown}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-stone-700 rounded-lg hover:bg-stone-50 transition-colors duration-200"
                  >
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#d4b5a0' }}
                    >
                      <User size={16} className="text-stone-700" />
                    </div>
                    <span className="max-w-32 truncate">
                      {user.displayName || user.email?.split('@')[0] || 'User'}
                    </span>
                    <ChevronDown size={16} className={`transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {profileDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-stone-200 py-2 z-10">
                     {/* <Link
                        to="/profile"
                        onClick={() => {
                          setActiveTab('profile');
                          setProfileDropdownOpen(false);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 transition-colors duration-200"
                      >
                        <User size={16} />
                        <span>Profile</span>
                      </Link> */}
                      <hr className="my-1 border-stone-200" />
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <LogOut size={16} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  )} 
                </div>
              </div>
            ) : (
              <div className="hidden lg:flex items-center space-x-3">
                <Link
                  to="/signup"
                  className="px-6 py-2 text-sm font-medium text-white rounded-lg transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md tracking-wider uppercase"
                  style={{ 
                    backgroundColor: '#8b7355',
                    '&:hover': { backgroundColor: '#7a6347' }
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#7a6347'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#8b7355'}
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-stone-600 hover:text-stone-800 hover:bg-stone-50 rounded-lg transition-colors duration-200"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <div className="relative w-6 h-6">
                <Menu 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-200 ${mobileMenuOpen ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'}`} 
                />
                <X 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-200 ${mobileMenuOpen ? 'opacity-100 rotate-0' : 'opacity-0 -rotate-90'}`} 
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div 
        className={`lg:hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div 
          ref={mobileMenuRef}
          className="bg-white border-t border-stone-200 shadow-sm"
        >
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {/* Navigation Links */}
            {[
              { to: '/', label: 'Home', tab: 'home' },
             /* { to: '/howitworks', label: 'How It Works', tab: 'how' },
              { to: '/pricing', label: 'Pricing', tab: 'pricing' } */
            ].map((item) => (
              <Link
                key={item.tab}
                to={item.to}
                onClick={() => handleMobileNavClick(item.tab)}
                className={`block px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 tracking-wide ${
                  activeTab === item.tab 
                    ? 'bg-stone-100 text-stone-700' 
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-800'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile User Section */}
            {user ? (
              <>
                <hr className="my-3 border-stone-200" />
                
                {/* Mobile Messages and Search Links */}
                {/*<Link
                  to="/users"
                  onClick={() => handleMobileNavClick('users')}
                  className={`flex items-center space-x-3 px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                    activeTab === 'users' 
                      ? 'bg-stone-100 text-stone-700' 
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <Search size={18} />
                  <span>Search Users</span>
                </Link> */}

                <Link
                  to="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3"
                  onClick={() => handleMobileNavClick('chat')}
                  className={`flex items-center space-x-3 px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                    activeTab === 'chat' 
                      ? 'bg-stone-100 text-stone-700' 
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <div className="relative">
                    <MessageCircle size={18} />
                   {/* In both desktop and mobile message links, update the badge */}
{unreadMessagesCount > 0 && (
  <div 
    className="absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full flex items-center justify-center text-xs font-bold text-white"
    style={{ backgroundColor: '#dc2626' }}
  >
    {unreadMessagesCount > 99 ? '99+' : unreadMessagesCount}
  </div>
)}
                  </div>
                  <span>Messages</span>
                </Link>

              {/*  <Link
                  to="/profile"
                  onClick={() => handleMobileNavClick('profile')}
                  className={`flex items-center space-x-3 px-4 py-3 text-base font-medium rounded-lg transition-colors duration-200 ${
                    activeTab === 'profile' 
                      ? 'bg-stone-100 text-stone-700' 
                      : 'text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#d4b5a0' }}
                  >
                    <User size={14} className="text-stone-700" />
                  </div>
                  <span>Profile</span>
                </Link> */}
                <button
                  onClick={handleSignOut}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                >
                  <LogOut size={18} />
                  <span>Sign Out</span>
                </button>
              </>
            ) : (
              <>
                <hr className="my-3 border-stone-200" />
                <Link
                  to="/signup"
                  onClick={() => handleMobileNavClick('signup')}
                  className="block w-full px-4 py-3 text-base font-medium text-center text-white rounded-lg transition-all duration-200 tracking-wider uppercase"
                  style={{ backgroundColor: '#8b7355' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#7a6347'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#8b7355'}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;